import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
} from "reactstrap";
import classnames from "classnames";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import {
  createNurtureCampaign,
  CsAtRiskTenant,
  CsNurtureSettings,
  getCsAtRiskTenants,
  getCsNurtureSettings,
  getNurtureCampaigns,
  getNurtureDeliveries,
  NurtureCampaign,
  NurtureCampaignCopy,
  NurtureCampaignKind,
  NurtureCampaignPreview,
  NurtureChannel,
  NurtureDeliveryRow,
  previewNurtureCampaign,
  updateCsNurtureSettings,
  updateNurtureCampaign,
  UpsertNurtureCampaignPayload,
} from "../../../../services/superAdmin/csNurtureService";

const emptyCopy = (): NurtureCampaignCopy => ({
  subject: "",
  subject_b: "",
  body: "",
  cta_label: "",
  push_title: "",
  push_body: "",
});

type CampaignForm = UpsertNurtureCampaignPayload;

const emptyForm = (): CampaignForm => ({
  slug: "",
  kind: "early_drip",
  name: "",
  target_gap: "no_aha",
  offset_hours: 24,
  priority: 100,
  deep_link: "/",
  deep_link_expo: "/",
  channels: ["email", "expo_push"],
  copy_pt_br: emptyCopy(),
  copy_en: emptyCopy(),
  copy_es: emptyCopy(),
  is_active: true,
  stop_on_aha: true,
});

const mapCampaignToForm = (campaign: NurtureCampaign): CampaignForm => ({
  slug: campaign.slug,
  kind: campaign.kind,
  name: campaign.name ?? "",
  target_gap: campaign.target_gap,
  offset_hours: campaign.offset_hours,
  priority: campaign.priority,
  benefit_key: campaign.benefit_key,
  deep_link: campaign.deep_link,
  deep_link_expo: campaign.deep_link_expo,
  channels: campaign.channels,
  copy_pt_br: { ...emptyCopy(), ...campaign.copy_pt_br },
  copy_en: { ...emptyCopy(), ...campaign.copy_en },
  copy_es: { ...emptyCopy(), ...campaign.copy_es },
  is_active: campaign.is_active,
  stop_on_aha: campaign.stop_on_aha,
});

const SuperAdminCsNurture = () => {
  const { t, i18n } = useTranslation();

  const [settings, setSettings] = useState<CsNurtureSettings | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [campaigns, setCampaigns] = useState<NurtureCampaign[]>([]);
  const [deliveries, setDeliveries] = useState<NurtureDeliveryRow[]>([]);
  const [atRisk, setAtRisk] = useState<CsAtRiskTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState<"" | NurtureCampaignKind>("");
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editing, setEditing] = useState<NurtureCampaign | null>(null);
  const [form, setForm] = useState<CampaignForm>(emptyForm());
  const [copyTab, setCopyTab] = useState<"pt_br" | "en" | "es">("pt_br");
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<NurtureCampaignPreview | null>(null);
  const [previewVariant, setPreviewVariant] = useState<"A" | "B">("A");

  useEffect(() => {
    document.title = t("admin.cs.nurture.pageTitle");
  }, [t, i18n.language]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, campaignsRes, deliveriesRes, atRiskRes] =
        await Promise.all([
          getCsNurtureSettings(),
          getNurtureCampaigns(kindFilter ? { kind: kindFilter } : undefined),
          getNurtureDeliveries({ per_page: 15, page: 1 }),
          getCsAtRiskTenants({ limit: 30 }),
        ]);
      setSettings(settingsRes.data);
      setCampaigns(campaignsRes.data);
      setDeliveries(deliveriesRes.data);
      setAtRisk(atRiskRes.data);
    } catch (error: any) {
      toast.error(error?.data || t("admin.cs.nurture.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [kindFilter, t]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setCopyTab("pt_br");
    setOffcanvasOpen(true);
  };

  const openEdit = useCallback((campaign: NurtureCampaign) => {
    setEditing(campaign);
    setForm(mapCampaignToForm(campaign));
    setCopyTab("pt_br");
    setOffcanvasOpen(true);
  }, []);

  const toggleChannel = (channel: NurtureChannel) => {
    setForm((prev) => {
      const has = prev.channels.includes(channel);
      const channels = has
        ? prev.channels.filter((c) => c !== channel)
        : [...prev.channels, channel];
      return { ...prev, channels: channels.length ? channels : ["email"] };
    });
  };

  const updateCopyField = (
    locale: "copy_pt_br" | "copy_en" | "copy_es",
    field: keyof NurtureCampaignCopy,
    value: string
  ) => {
    setForm((prev) => ({
      ...prev,
      [locale]: { ...prev[locale], [field]: value },
    }));
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSettingsSaving(true);
    try {
      const response = await updateCsNurtureSettings(settings);
      setSettings(response.data);
      toast.success(t("admin.cs.nurture.toast.settingsSaved"));
    } catch (error: any) {
      toast.error(error?.data || t("admin.cs.nurture.toast.settingsError"));
    } finally {
      setSettingsSaving(false);
    }
  };

  const openPreview = useCallback(
    async (campaign: NurtureCampaign, variant: "A" | "B" = "A") => {
      setPreviewLoading(true);
      setPreviewOpen(true);
      setPreviewVariant(variant);
      try {
        const response = await previewNurtureCampaign(campaign.id, {
          locale: "pt-BR",
          variant,
        });
        setPreview(response.data);
      } catch (error: any) {
        toast.error(error?.data || t("admin.cs.nurture.toast.previewError"));
        setPreviewOpen(false);
      } finally {
        setPreviewLoading(false);
      }
    },
    [t]
  );

  const saveCampaign = async () => {
    setSubmitting(true);
    try {
      const payload: UpsertNurtureCampaignPayload = {
        ...form,
        offset_hours: form.kind === "early_drip" ? form.offset_hours : null,
      };
      if (editing) {
        await updateNurtureCampaign(editing.id, payload);
        toast.success(t("admin.cs.nurture.toast.campaignUpdated"));
      } else {
        await createNurtureCampaign(payload);
        toast.success(t("admin.cs.nurture.toast.campaignCreated"));
      }
      setOffcanvasOpen(false);
      await loadAll();
    } catch (error: any) {
      toast.error(error?.data || t("admin.cs.nurture.toast.campaignError"));
    } finally {
      setSubmitting(false);
    }
  };

  const campaignColumns = useMemo<ColumnDef<NurtureCampaign>[]>(
    () => [
      {
        header: t("admin.cs.nurture.table.name"),
        accessorKey: "name",
        cell: ({ row }) => (
          <div>
            <div className="fw-medium">{row.original.name || row.original.slug}</div>
            <small className="text-muted">{row.original.slug}</small>
          </div>
        ),
      },
      {
        header: t("admin.cs.nurture.table.kind"),
        accessorKey: "kind",
        cell: ({ row }) => (
          <Badge color={row.original.kind === "early_drip" ? "primary" : "info"}>
            {t(`admin.cs.nurture.kind.${row.original.kind}`)}
          </Badge>
        ),
      },
      {
        header: t("admin.cs.nurture.table.timing"),
        cell: ({ row }) =>
          row.original.kind === "early_drip"
            ? `${row.original.offset_hours ?? "—"}h`
            : t("admin.cs.nurture.timing.weekly"),
      },
      {
        header: t("admin.cs.nurture.table.gap"),
        accessorKey: "target_gap",
      },
      {
        header: t("admin.cs.nurture.table.status"),
        cell: ({ row }) => (
          <Badge color={row.original.is_active ? "success" : "secondary"}>
            {row.original.is_active
              ? t("admin.cs.nurture.status.active")
              : t("admin.cs.nurture.status.inactive")}
          </Badge>
        ),
      },
      {
        header: t("admin.cs.nurture.table.actions"),
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button color="soft-primary" size="sm" onClick={() => openEdit(row.original)}>
              {t("admin.cs.nurture.actions.edit")}
            </Button>
            <Button
              color="soft-secondary"
              size="sm"
              onClick={() => void openPreview(row.original, "A")}
            >
              {t("admin.cs.nurture.actions.preview")}
            </Button>
          </div>
        ),
      },
    ],
    [t, openEdit, openPreview]
  );

  const copyLocaleKey =
    copyTab === "pt_br" ? "copy_pt_br" : copyTab === "en" ? "copy_en" : "copy_es";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("admin.cs.nurture.breadcrumb.title")}
          pageTitle={t("admin.cs.nurture.breadcrumb.pageTitle")}
        />
        <ToastContainer />

        {loading && (
          <div className="text-center py-5">
            <Spinner />
          </div>
        )}

        {!loading && settings && (
          <>
            <Card className="mb-3">
              <CardHeader>
                <h5 className="mb-0">{t("admin.cs.nurture.settings.title")}</h5>
                <p className="text-muted mb-0 small">
                  {t("admin.cs.nurture.settings.subtitle")}
                </p>
              </CardHeader>
              <CardBody>
                <Row className="g-3">
                  <Col md={4}>
                    <FormGroup switch>
                      <Input
                        type="switch"
                        checked={settings.early_drip_enabled}
                        onChange={(e) =>
                          setSettings({ ...settings, early_drip_enabled: e.target.checked })
                        }
                      />
                      <Label check>{t("admin.cs.nurture.settings.earlyEnabled")}</Label>
                    </FormGroup>
                    <FormGroup switch>
                      <Input
                        type="switch"
                        checked={settings.weekly_nurture_enabled}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            weekly_nurture_enabled: e.target.checked,
                          })
                        }
                      />
                      <Label check>{t("admin.cs.nurture.settings.weeklyEnabled")}</Label>
                    </FormGroup>
                    <FormGroup switch>
                      <Input
                        type="switch"
                        checked={settings.expo_push_enabled}
                        onChange={(e) =>
                          setSettings({ ...settings, expo_push_enabled: e.target.checked })
                        }
                      />
                      <Label check>{t("admin.cs.nurture.settings.expoEnabled")}</Label>
                    </FormGroup>
                    <FormGroup switch>
                      <Input
                        type="switch"
                        checked={settings.stop_early_on_aha}
                        onChange={(e) =>
                          setSettings({ ...settings, stop_early_on_aha: e.target.checked })
                        }
                      />
                      <Label check>{t("admin.cs.nurture.settings.stopOnAha")}</Label>
                    </FormGroup>
                    <FormGroup switch>
                      <Input
                        type="switch"
                        checked={settings.quiet_hours_enabled}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            quiet_hours_enabled: e.target.checked,
                          })
                        }
                      />
                      <Label check>{t("admin.cs.nurture.settings.quietEnabled")}</Label>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <Label>{t("admin.cs.nurture.settings.earlyHour")}</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={settings.early_dispatch_hour_utc}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          early_dispatch_hour_utc: Number(e.target.value),
                        })
                      }
                    />
                    <Label className="mt-2">{t("admin.cs.nurture.settings.weeklyHour")}</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={settings.weekly_dispatch_hour_utc}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          weekly_dispatch_hour_utc: Number(e.target.value),
                        })
                      }
                    />
                    <Label className="mt-2">{t("admin.cs.nurture.settings.weeklyWeekday")}</Label>
                    <Input
                      type="select"
                      value={settings.weekly_dispatch_weekday}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          weekly_dispatch_weekday: Number(e.target.value),
                        })
                      }
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                        <option key={day} value={day}>
                          {t(`admin.cs.nurture.weekday.${day}`)}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={4}>
                    <Label>{t("admin.cs.nurture.settings.maxEarly")}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={14}
                      value={settings.max_early_per_tenant_per_week}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          max_early_per_tenant_per_week: Number(e.target.value),
                        })
                      }
                    />
                    <Label className="mt-2">{t("admin.cs.nurture.settings.cooldown")}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={settings.cooldown_days}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          cooldown_days: Number(e.target.value),
                        })
                      }
                    />
                    <Label className="mt-2">{t("admin.cs.nurture.settings.quietStart")}</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={settings.quiet_hours_start_local}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          quiet_hours_start_local: Number(e.target.value),
                        })
                      }
                    />
                    <Label className="mt-2">{t("admin.cs.nurture.settings.quietEnd")}</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={settings.quiet_hours_end_local}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          quiet_hours_end_local: Number(e.target.value),
                        })
                      }
                    />
                    <Label className="mt-2">{t("admin.cs.nurture.settings.atRiskHours")}</Label>
                    <Input
                      type="number"
                      min={12}
                      max={336}
                      value={settings.at_risk_inactive_hours}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          at_risk_inactive_hours: Number(e.target.value),
                        })
                      }
                    />
                    <Button
                      color="primary"
                      className="mt-3"
                      disabled={settingsSaving}
                      onClick={() => void saveSettings()}
                    >
                      {settingsSaving ? <Spinner size="sm" /> : t("admin.cs.nurture.actions.saveSettings")}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardHeader>
                <h5 className="mb-0">{t("admin.cs.nurture.atRisk.title")}</h5>
                <p className="text-muted mb-0 small">
                  {t("admin.cs.nurture.atRisk.subtitle", {
                    hours: settings.at_risk_inactive_hours,
                  })}
                </p>
              </CardHeader>
              <CardBody>
                {atRisk.length === 0 ? (
                  <p className="text-muted mb-0">{t("admin.cs.nurture.atRisk.empty")}</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("admin.cs.nurture.atRisk.tenant")}</th>
                          <th>{t("admin.cs.nurture.atRisk.owner")}</th>
                          <th>{t("admin.cs.nurture.atRisk.reason")}</th>
                          <th>{t("admin.cs.nurture.atRisk.earlySent")}</th>
                          <th>{t("admin.cs.nurture.atRisk.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {atRisk.map((row) => (
                          <tr key={row.tenant_id}>
                            <td>
                              <div className="fw-medium">{row.tenant_name}</div>
                              <small className="text-muted">
                                {row.hours_since_trial != null
                                  ? t("admin.cs.nurture.atRisk.hoursSinceTrial", {
                                      hours: row.hours_since_trial,
                                    })
                                  : "—"}
                              </small>
                            </td>
                            <td>
                              <div>{row.owner_name || "—"}</div>
                              <small className="text-muted">{row.owner_email || "—"}</small>
                            </td>
                            <td>
                              <Badge color={row.reason === "never_logged_in" ? "danger" : "warning"}>
                                {t(`admin.cs.nurture.atRisk.reasons.${row.reason}`)}
                              </Badge>
                            </td>
                            <td>{row.early_deliveries_sent}</td>
                            <td>
                              <Button
                                tag="a"
                                href={`/super-admin/tenants?tenant_id=${row.tenant_id}`}
                                color="soft-primary"
                                size="sm"
                              >
                                {t("admin.cs.nurture.atRisk.openTenant")}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h5 className="mb-0">{t("admin.cs.nurture.campaigns.title")}</h5>
                </div>
                <div className="d-flex gap-2">
                  <Input
                    type="select"
                    style={{ width: 180 }}
                    value={kindFilter}
                    onChange={(e) =>
                      setKindFilter(e.target.value as "" | NurtureCampaignKind)
                    }
                  >
                    <option value="">{t("admin.cs.nurture.filters.allKinds")}</option>
                    <option value="early_drip">{t("admin.cs.nurture.kind.early_drip")}</option>
                    <option value="weekly_gap">{t("admin.cs.nurture.kind.weekly_gap")}</option>
                  </Input>
                  <Button color="primary" onClick={openCreate}>
                    {t("admin.cs.nurture.actions.newCampaign")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <TableContainer
                  columns={campaignColumns}
                  data={campaigns}
                  customPageSize={20}
                  tableClass="table-nowrap align-middle mb-0"
                  theadClass="table-light"
                  isGlobalFilter={false}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="mb-0">{t("admin.cs.nurture.deliveries.title")}</h5>
              </CardHeader>
              <CardBody>
                {deliveries.length === 0 ? (
                  <p className="text-muted mb-0">{t("admin.cs.nurture.deliveries.empty")}</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("admin.cs.nurture.deliveries.tenant")}</th>
                          <th>{t("admin.cs.nurture.deliveries.campaign")}</th>
                          <th>{t("admin.cs.nurture.deliveries.channel")}</th>
                          <th>{t("admin.cs.nurture.deliveries.status")}</th>
                          <th>{t("admin.cs.nurture.deliveries.when")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries.map((row) => (
                          <tr key={row.id}>
                            <td>{row.tenant_name || row.tenant_id}</td>
                            <td>{row.campaign_slug || "—"}</td>
                            <td>{row.channel}</td>
                            <td>
                              {row.sent_at
                                ? t("admin.cs.nurture.deliveries.sent")
                                : row.skipped_reason || "—"}
                            </td>
                            <td>
                              {row.sent_at || row.created_at
                                ? new Intl.DateTimeFormat(i18n.language, {
                                    dateStyle: "short",
                                    timeStyle: "short",
                                  }).format(new Date(row.sent_at || row.created_at))
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </>
        )}

        <Offcanvas
          direction="end"
          isOpen={offcanvasOpen}
          toggle={() => setOffcanvasOpen(false)}
          style={{ width: 520 }}
        >
          <OffcanvasHeader toggle={() => setOffcanvasOpen(false)}>
            {editing
              ? t("admin.cs.nurture.form.editTitle")
              : t("admin.cs.nurture.form.createTitle")}
          </OffcanvasHeader>
          <OffcanvasBody>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.name")}</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.slug")}</Label>
              <Input
                value={form.slug}
                disabled={Boolean(editing)}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.kind")}</Label>
              <Input
                type="select"
                value={form.kind}
                onChange={(e) =>
                  setForm({ ...form, kind: e.target.value as NurtureCampaignKind })
                }
              >
                <option value="early_drip">{t("admin.cs.nurture.kind.early_drip")}</option>
                <option value="weekly_gap">{t("admin.cs.nurture.kind.weekly_gap")}</option>
              </Input>
            </FormGroup>
            {form.kind === "early_drip" && (
              <FormGroup>
                <Label>{t("admin.cs.nurture.form.offsetHours")}</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.offset_hours ?? 24}
                  onChange={(e) =>
                    setForm({ ...form, offset_hours: Number(e.target.value) })
                  }
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.targetGap")}</Label>
              <Input
                value={form.target_gap}
                onChange={(e) => setForm({ ...form, target_gap: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.priority")}</Label>
              <Input
                type="number"
                value={form.priority}
                onChange={(e) =>
                  setForm({ ...form, priority: Number(e.target.value) })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.deepLink")}</Label>
              <Input
                value={form.deep_link}
                onChange={(e) => setForm({ ...form, deep_link: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.deepLinkExpo")}</Label>
              <Input
                value={form.deep_link_expo ?? ""}
                onChange={(e) =>
                  setForm({ ...form, deep_link_expo: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label className="d-block">{t("admin.cs.nurture.form.channels")}</Label>
              {(["email", "expo_push"] as NurtureChannel[]).map((channel) => (
                <FormGroup switch key={channel} className="mb-1">
                  <Input
                    type="switch"
                    checked={form.channels.includes(channel)}
                    onChange={() => toggleChannel(channel)}
                  />
                  <Label check>{channel}</Label>
                </FormGroup>
              ))}
            </FormGroup>
            <FormGroup switch>
              <Input
                type="switch"
                checked={Boolean(form.is_active)}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              <Label check>{t("admin.cs.nurture.form.isActive")}</Label>
            </FormGroup>
            <FormGroup switch>
              <Input
                type="switch"
                checked={Boolean(form.stop_on_aha)}
                onChange={(e) => setForm({ ...form, stop_on_aha: e.target.checked })}
              />
              <Label check>{t("admin.cs.nurture.form.stopOnAha")}</Label>
            </FormGroup>

            <Nav tabs className="mb-3">
              {(["pt_br", "en", "es"] as const).map((tab) => (
                <NavItem key={tab}>
                  <NavLink
                    className={classnames({ active: copyTab === tab })}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCopyTab(tab);
                    }}
                  >
                    {tab.toUpperCase()}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <FormGroup>
              <Label>{t("admin.cs.nurture.form.subject")}</Label>
              <Input
                value={form[copyLocaleKey].subject}
                onChange={(e) =>
                  updateCopyField(copyLocaleKey, "subject", e.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.subjectB")}</Label>
              <Input
                value={form[copyLocaleKey].subject_b ?? ""}
                onChange={(e) =>
                  updateCopyField(copyLocaleKey, "subject_b", e.target.value)
                }
              />
              <small className="text-muted">{t("admin.cs.nurture.form.subjectBHint")}</small>
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.body")}</Label>
              <Input
                type="textarea"
                rows={5}
                value={form[copyLocaleKey].body}
                onChange={(e) => updateCopyField(copyLocaleKey, "body", e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.cta")}</Label>
              <Input
                value={form[copyLocaleKey].cta_label}
                onChange={(e) =>
                  updateCopyField(copyLocaleKey, "cta_label", e.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.pushTitle")}</Label>
              <Input
                value={form[copyLocaleKey].push_title ?? ""}
                onChange={(e) =>
                  updateCopyField(copyLocaleKey, "push_title", e.target.value)
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>{t("admin.cs.nurture.form.pushBody")}</Label>
              <Input
                value={form[copyLocaleKey].push_body ?? ""}
                onChange={(e) =>
                  updateCopyField(copyLocaleKey, "push_body", e.target.value)
                }
              />
            </FormGroup>

            <div className="d-flex flex-wrap gap-2">
              <Button color="primary" disabled={submitting} onClick={() => void saveCampaign()}>
                {submitting ? <Spinner size="sm" /> : t("admin.cs.nurture.actions.saveCampaign")}
              </Button>
              {editing ? (
                <Button
                  color="soft-secondary"
                  disabled={previewLoading}
                  onClick={() => void openPreview(editing, previewVariant)}
                >
                  {t("admin.cs.nurture.actions.preview")}
                </Button>
              ) : null}
            </div>
          </OffcanvasBody>
        </Offcanvas>

        <Modal isOpen={previewOpen} toggle={() => setPreviewOpen(false)} size="lg">
          <ModalHeader toggle={() => setPreviewOpen(false)}>
            {t("admin.cs.nurture.preview.title")}
          </ModalHeader>
          <ModalBody>
            <div className="d-flex gap-2 mb-3">
              {(["A", "B"] as const).map((variant) => (
                <Button
                  key={variant}
                  size="sm"
                  color={previewVariant === variant ? "primary" : "soft-secondary"}
                  disabled={!editing || previewLoading}
                  onClick={() => {
                    if (editing) {
                      void openPreview(editing, variant);
                    }
                  }}
                >
                  {t("admin.cs.nurture.preview.variant", { variant })}
                </Button>
              ))}
            </div>
            {previewLoading || !preview ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : (
              <>
                <p className="fw-medium mb-2">{preview.subject}</p>
                <div
                  className="border rounded bg-light p-2"
                  style={{ maxHeight: 480, overflow: "auto" }}
                  dangerouslySetInnerHTML={{ __html: preview.html }}
                />
              </>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default SuperAdminCsNurture;
